import { useEffect, useState, useCallback, useMemo } from "react";
import Rack from "./Rack";
import httpService from "../../../services/http/stores/outlet.service";
import shelfHttpService from "../../../services/http/shelf-management/shelf.service";
import { useNavigate, useParams } from "react-router-dom";
import notificationService from "../../../services/toaster.service";
import UnlimitLoading from "../../../components/ui-kit/unlimit-loading";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Eye, Search, X } from "feather-icons-react/build/IconComponents";

const RackList = () => {
  const location = useLocation();
  const { name, image } = location.state || {};
  const [racks, setRacks] = useState([]);
  const [originalRacks, setOriginalRacks] = useState([]); // Store original data
  const [loading, setLoading] = useState(false);
  const [cctvList, setCCTVList] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { id: outletId } = useParams();
  const { t } = useTranslation();

  // Memoized outlet display info
  const outletDisplayInfo = useMemo(() => {
    if (name && image) {
      return { name, image };
    }
    return null;
  }, [name, image]);

  // Filter racks based on search term
  const filteredRacks = useMemo(() => {
    if (!searchTerm.trim()) {
      return originalRacks;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    return (
      originalRacks
        .map((rack) => ({
          ...rack,
          shelves: (rack.shelves || []).map((shelf) => ({
            ...shelf,
            sensors: (shelf.sensors || []).map((sensor) => ({
              ...sensor,
              products: (sensor.products || []).filter(
                (product) =>
                  String(product.lm_product_number)
                    ?.toLowerCase()
                    .includes(searchLower) ||
                  String(product.ean)?.toLowerCase().includes(searchLower)
              ),
            })),
          })),
        }))
        // Only include racks that have at least one matching product
        .filter((rack) =>
          rack.shelves?.some((shelf) =>
            shelf.sensors?.some((sensor) =>
              sensor.products?.some((product) =>
                String(product.lm_product_number)
                  ?.toLowerCase()
                  .includes(searchLower)
              )
            )
          )
        )
    );
  }, [originalRacks, searchTerm]);

  // Update racks when filtered data changes
  useEffect(() => {
    setRacks(filteredRacks);
  }, [filteredRacks]);

  // Fetch outlet data with better error handling
  const fetchOutletData = useCallback(async () => {
    if (!outletId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await shelfHttpService.getSaveProduct(outletId);

      if (response?.status === 200 && response.data?.data) {
        const processedRacks = response.data.data.map((rack) => ({
          ...rack,
          shelves: (rack.shelves || []).map((shelf) => ({
            ...shelf,
            sensors: (shelf.sensors || []).map((sensor) => ({
              ...sensor,
              products: sensor.products || [],
            })),
          })),
        }));

        setOriginalRacks(processedRacks);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Error fetching outlet data:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch outlet data";
      setError(`${errorMessage}. Please try again later.`);
      notificationService.errorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  // Fetch CCTV data
  const fetchCCTVData = useCallback(async () => {
    if (!outletId) return;

    try {
      const response = await httpService.shopCameraList(outletId);

      if (response?.status === 200 && response.data?.data?.data) {
        setCCTVList(response.data.data.data);
      }
    } catch (error) {
      console.error("Error fetching CCTV data:", error);
      // Don't show error notification for CCTV as it's not critical
    }
  }, [outletId]);

  // Initial data loading
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchOutletData(), fetchCCTVData()]);
    };

    loadData();
  }, [fetchOutletData, fetchCCTVData]);

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Memoized rack count for performance
  const rackCount = useMemo(() => racks.length, [racks.length]);
  const originalRackCount = useMemo(
    () => originalRacks.length,
    [originalRacks.length]
  );

  // Calculate total products matching search
  const matchingProductsCount = useMemo(() => {
    if (!searchTerm.trim()) return 0;

    return racks.reduce(
      (total, rack) =>
        total +
          rack.shelves?.reduce(
            (shelfTotal, shelf) =>
              shelfTotal +
                shelf.sensors?.reduce(
                  (sensorTotal, sensor) =>
                    sensorTotal + (sensor.products?.length || 0),
                  0
                ) || 0,
            0
          ) || 0,
      0
    );
  }, [racks, searchTerm]);

  // Loading state
  if (loading && originalRacks.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <div className="d-flex flex-column align-items-center">
          <UnlimitLoading type="spin" color="#9A0000" height={60} width={60} />
          <div className="mt-2 text-muted">Loading shelf data...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && originalRacks.length === 0) {
    return (
      <div className="alert alert-danger" role="alert">
        <h6 className="alert-heading">Error Loading Data</h6>
        <p className="mb-2">{error}</p>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() => {
            setError(null);
            fetchOutletData();
          }}
        >
          {"Retry"}
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Outlet Information */}
      {outletDisplayInfo ? (
        <div className="d-flex align-items-center mb-2 p-2 rounded">
          <img
            src={outletDisplayInfo.image}
            alt={outletDisplayInfo.name}
            className="me-3 rounded"
            style={{ width: "80px", height: "80px", objectFit: "cover" }}
          />
          <div>
            <h5 className="mb-1 fw-bold">{outletDisplayInfo.name}</h5>
            <small className="text-muted">
              {originalRackCount} {originalRackCount === 1 ? "rack" : "racks"}{" "}
              configured
            </small>
          </div>
        </div>
      ) : (
        <div className="alert alert-info" role="alert">
          <small>No outlet information available.</small>
        </div>
      )}
      {/* Search Bar */}
      <div className="mb-3">
        <div className="row d-flex justify-content-end align-items-center">
          <div className="col-md-3">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search LM Product No or EAN"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search Results Info */}
        {searchTerm.trim() && (
          <div className="mt-2">
            <small className="text-muted">
              {matchingProductsCount > 0 ? (
                <>
                  Found {matchingProductsCount} product
                  {matchingProductsCount !== 1 ? "s" : ""} in {rackCount} rack
                  {rackCount !== 1 ? "s" : ""} matching &apos;{searchTerm}&apos;
                </>
              ) : (
                <>No products found matching &apos;{searchTerm}&apos;</>
              )}
            </small>
          </div>
        )}
      </div>
      {/* Error display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      {/* Racks List */}
      <div className="row">
        {racks.map((rack, index) => (
          <div key={rack.id || index} className="col-12 mb-3">
            <Rack
              id={rack.id}
              number={index + 1}
              name={rack.name}
              shelves={rack.shelves}
              cameras={rack.cameras}
              // Pass processor fields
              p_number={rack.p_number}
              p_ip={rack.p_ip}
              p_port={rack.p_port}
              p_min_weight_change={rack.p_min_weight_change}
              outlet_id={outletId}
              cctvList={cctvList}
              // Make rack read-only
              readOnly={true}
            />
          </div>
        ))}
      </div>
      {racks.length === 0 && !loading && (
        <div className="text-center py-5">
          <div className="text-muted mb-3">
            {searchTerm.trim() ? (
              <>
                <h6>No matching products found</h6>
                <p>
                  No products with product number containing &#39;{searchTerm}
                  &#39; were found.
                </p>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={clearSearch}
                >
                  Clear search to see all racks
                </button>
              </>
            ) : (
              <>
                <h6>No racks configured</h6>
                <p>No racks have been set up for this outlet yet.</p>
              </>
            )}
          </div>
        </div>
      )}
      {/* Statistics */}
      {originalRacks.length > 0 && (
        <div className="mt-2 p-3 rounded" style={{ background: "#f1f1f1ff" }}>
          <small className="text-muted">
            <strong>Statistics:</strong> {originalRackCount} total racks,{" "}
            {originalRacks.reduce(
              (total, rack) => total + (rack.shelves?.length || 0),
              0
            )}{" "}
            shelves,{" "}
            {originalRacks.reduce(
              (total, rack) =>
                total +
                (rack.shelves?.reduce(
                  (shelfTotal, shelf) =>
                    shelfTotal + (shelf.sensors?.length || 0),
                  0
                ) || 0),
              0
            )}{" "}
            sensors
            {searchTerm.trim() && (
              <>
                {" | "}
                <strong>Filtered:</strong> {rackCount} racks shown,{" "}
                {matchingProductsCount} products matching search
              </>
            )}
          </small>
        </div>
      )}
    </div>
  );
};

export default RackList;
